/**
 * Utilities for working with maps
 */

/**
 * Detect user platform
 * @returns {string} - 'ios' | 'android' | 'desktop'
 */
const detectPlatform = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // iOS detection
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  }

  // Android detection
  if (/android/i.test(userAgent)) {
    return 'android';
  }

  return 'desktop';
};

/**
 * Get Yandex Maps URL
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @param {string} placeName - Place name for marker
 * @returns {string} - Yandex Maps URL
 */
export const getYandexMapsUrl = (latitude, longitude, placeName = '') => {
  // Yandex Maps uses lon,lat order (opposite of typical lat,lon)
  return `https://yandex.ru/maps/?pt=${longitude},${latitude}&z=16&l=map&text=${encodeURIComponent(placeName)}`;
};

/**
 * Get Google Maps URL
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @param {string} placeName - Place name for marker
 * @returns {string} - Google Maps URL
 */
export const getGoogleMapsUrl = (latitude, longitude, placeName = '') => {
  return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}&query=${encodeURIComponent(placeName)}`;
};

/**
 * Get Apple Maps URL (for iOS)
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @param {string} placeName - Place name
 * @returns {string} - Apple Maps URL
 */
export const getAppleMapsUrl = (latitude, longitude, placeName = '') => {
  return `maps://maps.apple.com/?q=${encodeURIComponent(placeName)}&ll=${latitude},${longitude}&z=16`;
};

/**
 * Get appropriate map URL based on platform
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @param {string} placeName - Place name
 * @param {string} preferredMap - 'yandex' | 'google' | 'auto' (default: 'yandex')
 * @returns {string} - Map URL
 */
export const getMapUrl = (latitude, longitude, placeName = '', preferredMap = 'yandex') => {
  const platform = detectPlatform();

  // For Russia, prefer Yandex Maps
  if (preferredMap === 'yandex' || preferredMap === 'auto') {
    return getYandexMapsUrl(latitude, longitude, placeName);
  }

  // iOS can use Apple Maps
  if (platform === 'ios' && preferredMap === 'apple') {
    return getAppleMapsUrl(latitude, longitude, placeName);
  }

  // Fallback to Google Maps
  return getGoogleMapsUrl(latitude, longitude, placeName);
};

/**
 * Open location in maps application
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @param {string} placeName - Place name
 * @param {string} preferredMap - Map preference (default: 'yandex')
 */
export const openInMaps = (latitude, longitude, placeName = '', preferredMap = 'yandex') => {
  if (!latitude || !longitude) {
    return;
  }

  const url = getMapUrl(latitude, longitude, placeName, preferredMap);

  // Open in browser via Telegram Web App API
  if (typeof window !== 'undefined' && window.Telegram?.WebApp?.openLink) {
    window.Telegram.WebApp.openLink(url);
  } else {
    // Fallback for development
    window.open(url, '_blank');
  }
};

/**
 * Get distance between two coordinates (Haversine formula)
 * @param {number} lat1 - First latitude
 * @param {number} lon1 - First longitude
 * @param {number} lat2 - Second latitude
 * @param {number} lon2 - Second longitude
 * @returns {number} - Distance in kilometers
 */
export const getDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance;
};

// Вторая formatDistance убрана отсюда: она принимала километры, а её
// тёзка в services/geolocation.js — метры, при одинаковом виде результата.
// Показ расстояния идёт через formatApproxDistance в utils/measures.js
